import { useToast } from '@lifebit-ai/lds'
import { useCallback, useMemo, useState } from 'react'

import { useGetAutomatedChecksReport } from 'services/results/hooks/useGetAutomatedChecksReport'
import { AutoCheckJobStatuses } from 'services/results/types'
import type { Result } from 'services/results/types'

import {
  formatAutomatedChecksDate,
  getAutomatedChecksJobs,
  getAutomatedChecksReportUrl,
  getChecksResult,
  getPipelineNameAndVersion
} from './utils'

interface UseAutomatedChecksReRunParams {
  resultId: string
  automatedChecks?: Result['automatedChecks']
  fileChangeStatus?: Result['fileChangeStatus']
  workspaceName?: string
}

interface SelectedReportPreview {
  fileKey: string
  signedUrl: string
}

interface UseAutomatedChecksReRunReturn {
  checksResult: {
    label: string
    showStatusIcon: boolean
  }
  formattedRunDate: string
  pipelineNameAndVersion: string
  reportUrl?: string
  isAutomatedChecksReportVisible: boolean
  isAutoCheckJobRunning: boolean
  isAutoCheckJobFailed: boolean
  approvalMethodLabel: string
  selectedReportPreview: SelectedReportPreview | null
  workspace: string
  onClickViewAutomatedChecksReport: (reportUrl: string) => Promise<void>
  onCloseReportPreviewModal: () => void
  isDownloadingReport: boolean
  automatedChecksSkippedMessage: string
}

const getAutomatedChecksSkippedMessage = ({
  automatedChecks,
  fileChangeStatus
}: Pick<UseAutomatedChecksReRunParams, 'automatedChecks' | 'fileChangeStatus'>): string => {
  if (automatedChecks?.mode !== 'skipped') return ''
  if (fileChangeStatus === 'unchanged') {
    return 'Automated checks were not required. The file was verified as identical to the previously approved version.'
  }
  return 'Automated checks were not triggered for this request.'
}

const useAutomatedChecksReRun = ({
  resultId,
  automatedChecks,
  fileChangeStatus,
  workspaceName
}: UseAutomatedChecksReRunParams): UseAutomatedChecksReRunReturn | null => {
  const toast = useToast()
  const [selectedReportPreview, setSelectedReportPreview] = useState<SelectedReportPreview | null>(null)
  const reportUrl = useMemo(() => getAutomatedChecksReportUrl(automatedChecks), [automatedChecks])

  const { refetch: fetchAutomatedChecksReport, isFetching: isDownloadingReport } = useGetAutomatedChecksReport({
    resultId,
    jobId: reportUrl ?? '',
    options: {
      enabled: false,
      retry: false
    }
  })

  const onClickViewAutomatedChecksReport = useCallback(
    async (_reportUrl: string) => {
      try {
        const reportResponse = await fetchAutomatedChecksReport()
        const reportData = reportResponse.data

        if (!reportData?.signedUrl) {
          toast({
            message: 'Your download failed. If this issue persists, please contact your administrator.',
            type: 'error'
          })
          return
        }

        setSelectedReportPreview({
          fileKey: reportData.fileKey,
          signedUrl: reportData.signedUrl
        })
      } catch {
        toast({
          message: 'Your download failed. If this issue persists, please contact your administrator.',
          type: 'error'
        })
      }
    },
    [fetchAutomatedChecksReport, toast]
  )

  const onCloseReportPreviewModal = useCallback(() => {
    setSelectedReportPreview(null)
  }, [])

  return useMemo(() => {
    if (!automatedChecks) return null

    const firstAutoCheckJobStatus = getAutomatedChecksJobs(automatedChecks)[0]?.status
    const isAutoCheckJobRunning = firstAutoCheckJobStatus === AutoCheckJobStatuses.Pending
    const isAutoCheckJobFailed = firstAutoCheckJobStatus === AutoCheckJobStatuses.Failed

    return {
      checksResult: isAutoCheckJobRunning
        ? {
            label: 'In progress',
            showStatusIcon: false
          }
        : getChecksResult({ automatedChecks }),
      reportUrl,
      isDownloadingReport,
      automatedChecksSkippedMessage: getAutomatedChecksSkippedMessage({
        automatedChecks,
        fileChangeStatus
      }),
      isAutomatedChecksReportVisible: !isAutoCheckJobRunning,
      isAutoCheckJobRunning,
      isAutoCheckJobFailed,
      approvalMethodLabel: isAutoCheckJobRunning ? 'In progress' : '',
      selectedReportPreview,
      onClickViewAutomatedChecksReport,
      onCloseReportPreviewModal,
      workspace: workspaceName ?? '-',
      formattedRunDate: formatAutomatedChecksDate(automatedChecks),
      pipelineNameAndVersion: getPipelineNameAndVersion(automatedChecks)
    }
  }, [
    automatedChecks,
    fileChangeStatus,
    onClickViewAutomatedChecksReport,
    onCloseReportPreviewModal,
    reportUrl,
    selectedReportPreview,
    workspaceName,
    isDownloadingReport
  ])
}

export default useAutomatedChecksReRun
